import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  BarChart3, Package, ShoppingCart, DollarSign, TrendingUp, 
  Eye, Star, Clock, AlertTriangle, CheckCircle, Users,
  ArrowUp, ArrowDown, Plus, RefreshCw, MessageSquare, FileText, Bell
} from 'lucide-react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { supabase } from '../../lib/supabase';

const SellerDashboard = () => {
  const { user } = useSupabase();
  const [timeRange, setTimeRange] = useState('7d');
  const [supplierData, setSupplierData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [orders] = useState<any[]>([
    {
      id: 'ORD-001',
      date: '2024-02-28',
      customer: 'John Smith',
      product: 'Premium Cotton T-Shirt',
      quantity: 50,
      amount: 1499.50,
      status: 'processing'
    },
    {
      id: 'ORD-002',
      date: '2024-02-25',
      customer: 'Sarah Johnson',
      product: 'Handwoven Jute Bag',
      quantity: 100,
      amount: 1599.00,
      status: 'shipped'
    },
    {
      id: 'ORD-003',
      date: '2024-02-22',
      customer: 'Michael Chen',
      product: 'Traditional Jamdani Saree',
      quantity: 10,
      amount: 899.90,
      status: 'delivered'
    }
  ]);
  const [products] = useState<any[]>([
    {
      id: 'prod-1',
      name: 'Premium Cotton T-Shirt',
      sales: 245,
      revenue: '$7,350'
    },
    {
      id: 'prod-2',
      name: 'Handwoven Jute Bag',
      sales: 189,
      revenue: '$3,780'
    },
    {
      id: 'prod-3',
      name: 'Traditional Jamdani Saree',
      sales: 156,
      revenue: '$14,040'
    }
  ]);

  // Fetch supplier data
  React.useEffect(() => {
    const fetchSupplierData = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('suppliers')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (error) throw error;
        setSupplierData(data);
      } catch (err) {
        console.error('Error fetching supplier data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSupplierData();
  }, [user]);

  const stats = [
    {
      title: 'Total Revenue',
      value: '$45,600',
      change: '+12.5%',
      trend: 'up',
      icon: DollarSign,
      color: 'text-green-600 bg-green-100'
    },
    {
      title: 'Total Orders',
      value: orders.length.toString(),
      change: '+8.2%',
      trend: 'up',
      icon: ShoppingCart,
      color: 'text-blue-600 bg-blue-100'
    },
    {
      title: 'Products Listed',
      value: '6',
      change: '+3.1%',
      trend: 'up',
      icon: Package,
      color: 'text-purple-600 bg-purple-100'
    },
    {
      title: 'Avg. Rating',
      value: '4.8',
      change: '+0.2',
      trend: 'up',
      icon: Star,
      color: 'text-yellow-600 bg-yellow-100'
    }
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </span>;
      case 'processing':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Package className="h-3 w-3 mr-1" />
          Processing
        </span>;
      case 'shipped':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <RefreshCw className="h-3 w-3 mr-1" />
          Shipped
        </span>;
      case 'delivered':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Delivered
        </span>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Seller Dashboard</h1>
              <p className="text-gray-600">
                Welcome back, {supplierData?.organization || 'Seller'}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500"
              >
                <option value="24h">Last 24 hours</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>
              <Link 
                to="/seller-central/products/add"
                className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add Product</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                  <div className={`flex items-center mt-2 text-sm ${
                    stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stat.trend === 'up' ? (
                      <ArrowUp className="h-4 w-4 mr-1" />
                    ) : (
                      <ArrowDown className="h-4 w-4 mr-1" />
                    )}
                    <span>{stat.change}</span>
                  </div>
                </div>
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Revenue Chart */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Revenue Overview</h2>
                <div className="flex items-center space-x-2">
                  <button className="text-sm text-gray-600 hover:text-gray-900">Revenue</button>
                  <button className="text-sm text-gray-600 hover:text-gray-900">Orders</button>
                  <button className="text-sm text-gray-600 hover:text-gray-900">Products</button>
                </div>
              </div>
              <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">Chart visualization would go here</p>
                  <p className="text-sm text-gray-500">Integration with charting library needed</p>
                </div>
              </div>
            </div>

            {/* Recent Orders */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Recent Orders</h2>
                <button className="text-sm text-orange-600 hover:text-orange-700 font-medium">View All</button>
              </div>
              {orders.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <ShoppingCart className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No Orders Yet</h3>
                  <p className="text-gray-600 mb-4">You haven't received any orders yet.</p>
                  <Link 
                    to="/seller-central/products/add" 
                    className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors inline-block"
                  >
                    Add Your First Product
                  </Link>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">Order</th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">Customer</th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">Product</th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">Amount</th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {orders.map((order) => (
                        <tr key={order.id}>
                          <td className="py-4">
                            <div className="text-sm font-medium text-gray-900">{order.id}</div>
                            <div className="text-sm text-gray-500">{order.date}</div>
                          </td>
                          <td className="py-4 text-sm text-gray-900">{order.customer}</td>
                          <td className="py-4">
                            <div className="text-sm text-gray-900">{order.product}</div>
                            <div className="text-sm text-gray-500">Qty: {order.quantity}</div>
                          </td>
                          <td className="py-4 text-sm font-medium text-gray-900">${order.amount.toLocaleString()}</td>
                          <td className="py-4">{getStatusBadge(order.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Balance Section */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Balance</h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Available</span>
                  <span className="text-lg font-bold text-green-600">$0.00</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Pending</span>
                  <span className="text-lg font-medium text-yellow-600">$0.00</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Processing</span>
                  <span className="text-lg font-medium text-blue-600">$0.00</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">On Hold</span>
                  <span className="text-lg font-medium text-red-600">$0.00</span>
                </div>
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-200">
                <button className="w-full bg-green-600 text-white py-2 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center space-x-2">
                  <DollarSign className="h-4 w-4" />
                  <span>Withdraw</span>
                </button>
              </div>
            </div>

            {/* Top Products */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Top Performing Products</h2>
              {products.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No Products Yet</h3>
                  <p className="text-gray-600 mb-4">Add your first product to start selling.</p>
                  <Link 
                    to="/seller-central/products/add" 
                    className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors inline-block"
                  >
                    Add Product
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {products.map((product, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-gray-900">{product.name}</h3>
                        <p className="text-xs text-gray-600">{product.sales || 0} sales</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900">{product.revenue || '$0'}</p>
                        <div className="flex items-center text-xs text-gray-500">
                          <span>No data yet</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notifications */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Recent Activity</h2>
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">No Activity Yet</h3>
                <p className="text-gray-600">Your recent activity will appear here.</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-orange-50 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <Link 
                  to="/seller-central/products/add"
                  className="w-full bg-orange-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add New Product</span>
                </Link>
                <Link 
                  to="/seller-central/documents/prepare"
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <FileText className="h-4 w-4" />
                  <span>Prepare Documents</span>
                </Link>
                <Link 
                  to="/seller-central/orders"
                  className="w-full border border-orange-600 text-orange-600 py-2 px-4 rounded-lg text-sm font-medium hover:bg-orange-50 transition-colors flex items-center justify-center space-x-2"
                >
                  <Eye className="h-4 w-4" />
                  <span>View All Orders</span>
                </Link>
                <Link 
                  to="/seller-central/support"
                  className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
                >
                  <MessageSquare className="h-4 w-4" />
                  <span>Contact Support</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerDashboard;